import axios from 'axios';
import queryString from 'qs';
import cleanDeep from 'clean-deep';
import {isEmpty, isUndefined} from 'lodash/lang';
import {get} from 'lodash/object';
import Cookies from 'utils/cookies';
import {API_URL, IS_STAGE_DEV} from 'configs';

export const queryParse = (string) => {
  return queryString.parse(string, {ignoreQueryPrefix: true});
};

export const queryStringify = (obj, options) => {
  return queryString.stringify(obj, options);
};

export const makeSearchParams = (queries = {}) => {
  if (isEmpty(queries)) return {};

  if (typeof queries === 'string') {
    queries = queryParse(queries);
  }

  const {q, limit, offset, order, order_by, key, ...rest} = queries;

  let params = {
    q,
    limit: isUndefined(limit) ? 20 : limit,
    offset: isUndefined(offset) ? 0 : offset,
    key: isUndefined(key) ? '' : key,
  };

  if (!isUndefined(order)) params.order = order;
  if (!isUndefined(order_by)) params.order_by = order_by;
  if (!isEmpty(rest))
    params.filter = {
      ...rest,
    };

  return params;
};

/**
 * Global axios defaults
 */
export const setAxiosConfig = () => {
  // axios setup
  axios.defaults.baseURL = API_URL;
  axios.defaults.headers.common['x-http-bsid'] = Cookies.bsid; // bsid (browser unique ID)
  axios.defaults.withCredentials = true;
};

const http = (method = 'GET', url, data, options) => {
  const instance = axios.create();

  instance.interceptors.request.use(
    function (config) {
      // Set Request
      config.method = method;
      config.url = url;
      config.withCredentials = true;

      const bsid = Cookies.bsid || '';
      if (bsid) {
        config.headers['x-http-bsid'] = bsid;
      }

      const captchaToken = Cookies.captchaToken || '';
      if (captchaToken) {
        config.headers['x-http-captcha-token'] = captchaToken;
      }

      const csrfToken = Cookies.csrfToken || '';
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
      }

      if (method.toUpperCase() === 'GET') {
        const payload = cleanDeep(data);
        if (payload) config.params = payload;
      } else {
        const isFormData = data instanceof FormData;
        if (isFormData) {
          const isEmptyFormData = data.entries().next().done;
          if (!isEmptyFormData) {
            config.data = data; // FormData is not empty, so we need to send it as is
          }
        } else {
          const payload = cleanDeep(data);
          if (payload) config.data = payload;
        }
      }

      return {...config, ...options};
    },
    function (error) {
      // Do something with request error
      return Promise.reject(error);
    }
  );

  return instance()
    .then(({data}) => data)
    .catch((error) => {
      let result;
      const response = get(error, 'response', {});

      if (!isEmpty(response)) {
        const status = get(error, 'response.status', '');
        const data = get(error, 'response.data.data', {});
        const message = get(error, 'response.data.errors.message', '');
        const code = get(error, 'response.data.errors.code', '');
        const token = get(error, 'response.data.token', '');

        const errors = get(error, 'response.data.errors', {});

        result = {
          error: true,
          status: status,
        };

        if (Array.isArray(errors)) {
          result.errors = errors;
        } else {
          result = {
            ...result,
            ...errors,
          };
        }

        if (!isEmpty(data)) result.data = data;
        if (!isEmpty(message)) result.message = message;
        if (!isEmpty(token)) result.token = token;
        if (!isEmpty(code)) {
          result.code = status === 500 || status === '500' ? 'NOT_FOUND' : code; // Blocking to show 500 error.;
        }

        if (code === 'invalid_session_token') {
          Cookies.session = '';

          // @OLD: `/error/${status}`
          const redirectTo = `/login`;

          // @TODO: Redirect to login page with redirect url in the `src/utils/render-routes.js`
          window.location.replace(redirectTo);
        } else if (code === 'invalid_captcha_token') {
          Cookies.captchaToken = '';
        } else if (status === 404 || status === '404') {
          window.location.replace(`/error/${status}`);
        } else if (status === 500 || status === '500') {
          if (!IS_STAGE_DEV) window.location.replace(`/error/${status}`);
        }
      } else {
        result = error;
      }

      throw result;
    });
};

export default http;
